/** Pixel-per-minute constant for time-grid positioning. */
export const PX_PER_MIN = 1

/** Px-координати неробочої смуги в системі колонки (та сама, що bookingTop/bookingHeight). */
export interface OffWorkZone {
  topPx: number
  heightPx: number
}

/** Structural type: only the fields used for grid positioning math. */
interface BookingInterval {
  startsAt: string
  endsAt: string
}

/**
 * Projects a UTC instant to minutes-of-day in the given IANA timezone.
 * Uses Intl.DateTimeFormat to avoid manual offset arithmetic.
 * % 24 guard normalises "24" (midnight artefact in some Intl implementations).
 */
export function minutesOfDayInTz(isoUtc: string, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    hour: "numeric",
    minute: "numeric",
  }).formatToParts(new Date(isoUtc))
  const h = Number(parts.find((p) => p.type === "hour")!.value)
  const m = Number(parts.find((p) => p.type === "minute")!.value)
  return (h % 24) * 60 + m
}

/**
 * Absolute `top` offset (px) of a booking card inside the grid column.
 * Clamped to 0 for bookings starting before the grid open time (rare overtime).
 */
export function bookingTop(
  b: BookingInterval,
  gridStartMin: number,
  tz: string,
): number {
  const startMin = minutesOfDayInTz(b.startsAt, tz)
  return Math.max(0, (startMin - gridStartMin) * PX_PER_MIN)
}

/**
 * Height (px) of a booking card.
 * Enforces a `minPx` floor so very short services remain tappable.
 */
export function bookingHeight(
  b: BookingInterval,
  tz: string,
  minPx = 24,
): number {
  const startMin = minutesOfDayInTz(b.startsAt, tz)
  const endMin = minutesOfDayInTz(b.endsAt, tz)
  return Math.max((endMin - startMin) * PX_PER_MIN, minPx)
}

/**
 * Number of grid rows (1 row = 1 hour) between the given bounds.
 * Rounded up to accommodate a partial final row.
 */
export function gridRowCount(bounds: { startMin: number; endMin: number }): number {
  return Math.ceil((bounds.endMin - bounds.startMin) / 60)
}

/**
 * HH:MM labels for each hour row in the grid (one per row).
 * `tz` param is accepted for signature consistency; labels are already
 * in the salon's tz (bounds are projected before calling).
 */
export function hourLabels(
  bounds: { startMin: number; endMin: number },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _tz?: string,
): string[] {
  const labels: string[] = []
  const startHour = Math.floor(bounds.startMin / 60)
  const endHour = Math.floor(bounds.endMin / 60)
  for (let h = startHour; h < endHour; h++) {
    labels.push(`${String(h).padStart(2, "0")}:00`)
  }
  return labels
}

/**
 * Доповнення об'єднання робочих інтервалів майстра в межах гріда.
 * Повертає px-смуги неробочих зон (before-shift, split-shift gap, tail).
 * Чиста функція: без DOM, без Date.now().
 */
export function offWorkZones(
  intervals: { start: string; end: string }[],
  bounds: { startMin: number; endMin: number },
  tz: string,
): OffWorkZone[] {
  const { startMin, endMin } = bounds

  // Спроєктувати та клампити кожен інтервал до [startMin, endMin]
  const clamped = intervals
    .map((iv) => ({
      s: Math.max(minutesOfDayInTz(iv.start, tz), startMin),
      e: Math.min(minutesOfDayInTz(iv.end, tz), endMin),
    }))
    .filter((iv) => iv.s < iv.e)

  if (clamped.length === 0) {
    // Весь грід — неробочий (або інтервали повністю за межами)
    const h = (endMin - startMin) * PX_PER_MIN
    return h > 0 ? [{ topPx: 0, heightPx: h }] : []
  }

  // Злити перекривні інтервали
  clamped.sort((a, b) => a.s - b.s)
  const merged: { s: number; e: number }[] = [{ ...clamped[0] }]
  for (let i = 1; i < clamped.length; i++) {
    const last = merged[merged.length - 1]
    if (clamped[i].s <= last.e) {
      last.e = Math.max(last.e, clamped[i].e)
    } else {
      merged.push({ ...clamped[i] })
    }
  }

  // Побудувати доповнення — неробочі відрізки між [startMin, endMin]
  const zones: OffWorkZone[] = []
  let cursor = startMin

  for (const seg of merged) {
    if (cursor < seg.s) {
      zones.push({
        topPx: (cursor - startMin) * PX_PER_MIN,
        heightPx: (seg.s - cursor) * PX_PER_MIN,
      })
    }
    cursor = seg.e
  }

  if (cursor < endMin) {
    zones.push({
      topPx: (cursor - startMin) * PX_PER_MIN,
      heightPx: (endMin - cursor) * PX_PER_MIN,
    })
  }

  return zones
}
